/**
 * ChurchAttendancePane
 * ====================
 * Lives inside the Hierarchy Explorer's Leadership dialog as a sibling tab.
 * Two operating modes:
 *
 *   "Record"   — capture a single attendance session for the selected church
 *   "Roll-up"  — show aggregated attendance for the selected church + every
 *                descendant (HQ rolls up everything; an assembly rolls up
 *                its home groups; a home group shows just itself).
 *
 * Talks to:
 *   POST /api/churches-hx/attendance
 *   GET  /api/churches-hx/{id}/attendance-rollup
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const apiFetch = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

const MEETING_TYPES = [
  { value: 'service', label: 'Sunday Service' },
  { value: 'home_group', label: 'Home Group' },
  { value: 'cell', label: 'Cell Meeting' },
  { value: 'prayer_meeting', label: 'Prayer Meeting' },
  { value: 'conference', label: 'Conference' },
  { value: 'leadership_meeting', label: 'Leadership Meeting' },
  { value: 'other', label: 'Other' },
];

export const ChurchAttendancePane = ({ church }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    meeting_type: 'service',
    date: new Date().toISOString().slice(0, 10),
    title: '',
    total_present: 0,
    notes: '',
  });
  const [filter, setFilter] = useState({
    from_date: '',
    to_date: '',
  });

  const rollup = useQuery({
    queryKey: ['hx-attendance-rollup', church.id, filter.from_date, filter.to_date],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filter.from_date) params.append('from_date', filter.from_date);
      if (filter.to_date) params.append('to_date', filter.to_date);
      const qs = params.toString();
      return apiFetch(`/api/churches-hx/${church.id}/attendance-rollup${qs ? `?${qs}` : ''}`);
    },
  });

  const record = useMutation({
    mutationFn: () => apiFetch('/api/churches-hx/attendance', {
      method: 'POST',
      body: JSON.stringify({ church_id: church.id, ...form }),
    }),
    onSuccess: () => {
      toast.success('Attendance recorded');
      qc.invalidateQueries({ queryKey: ['hx-attendance-rollup', church.id] });
      setForm(f => ({ ...f, title: '', total_present: 0, notes: '' }));
    },
    onError: (e) => toast.error(e.message),
  });

  const r = rollup.data;
  return (
    <div data-testid="church-attendance-pane">
      <Tabs defaultValue="rollup">
        <TabsList>
          <TabsTrigger value="rollup" data-testid="att-tab-rollup">Roll-up</TabsTrigger>
          <TabsTrigger value="record" data-testid="att-tab-record">Record session</TabsTrigger>
        </TabsList>

        {/* Roll-up */}
        <TabsContent value="rollup" className="mt-3">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <Label className="text-[10px]">From date</Label>
              <Input type="date" value={filter.from_date} onChange={e => setFilter(f => ({ ...f, from_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-[10px]">To date</Label>
              <Input type="date" value={filter.to_date} onChange={e => setFilter(f => ({ ...f, to_date: e.target.value }))} />
            </div>
          </div>
          {rollup.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          ) : !r ? (
            <p className="text-sm text-slate-400">No data yet.</p>
          ) : (
            <div className="space-y-3">
              <Card><CardContent className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Grand total</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="att-grand-total">{r.grand_total_present}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Sessions</p>
                  <p className="text-2xl font-bold text-slate-800">{r.total_sessions}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">In scope</p>
                  <p className="text-2xl font-bold text-slate-800">{r.descendants_in_scope}</p>
                  <p className="text-[10px] text-slate-400">church{r.descendants_in_scope === 1 ? '' : 'es'}</p>
                </div>
              </CardContent></Card>

              {Object.keys(r.by_meeting_type || {}).length > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                      <TrendingUp className="h-3.5 w-3.5" /> By meeting type
                    </p>
                    <div className="space-y-1">
                      {Object.entries(r.by_meeting_type).map(([type, v]) => (
                        <div key={type} className="flex justify-between items-center text-sm">
                          <span className="capitalize text-slate-700">{type.replace('_', ' ')}</span>
                          <span>
                            <span className="font-semibold">{v.total_present}</span>
                            <span className="text-[10px] text-slate-400 ml-1">· {v.sessions} session{v.sessions === 1 ? '' : 's'}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(r.by_church || []).length > 1 && (
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" /> Top churches in your scope
                    </p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {(r.by_church || []).slice(0, 10).map(c => (
                        <div key={c.church_id} className="flex justify-between items-center text-sm">
                          <span className="truncate">
                            {c.church_name || c.church_id}
                            {c.church_level && <Badge variant="outline" className="ml-2 text-[10px] capitalize">{c.church_level.replace('_', ' ')}</Badge>}
                          </span>
                          <span className="font-semibold tabular-nums">{c.total_present}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Record session */}
        <TabsContent value="record" className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Meeting type</Label>
              <Select value={form.meeting_type} onValueChange={v => setForm(f => ({ ...f, meeting_type: v }))}>
                <SelectTrigger data-testid="att-meeting-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder='e.g. "Sunday 6am service"' />
            </div>
            <div>
              <Label>Total present</Label>
              <Input type="number" value={form.total_present} onChange={e => setForm(f => ({ ...f, total_present: parseInt(e.target.value) || 0 }))} data-testid="att-total-present" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <Button
            className="w-full"
            onClick={() => record.mutate()}
            disabled={record.isPending || !form.date || form.total_present < 0}
            data-testid="att-record-btn"
          >
            {record.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Record attendance
          </Button>
          <p className="text-[10px] text-slate-400">
            This count rolls up automatically. A district overseer will see this added to their district total.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChurchAttendancePane;
